import { useMemo, useState } from "react";
import { useGetList, useGetMany, useNotify, useUpdate } from "ra-core";
import { Link } from "react-router";
import { formatDistanceToNowStrict } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Flame,
  MoreHorizontal,
  RefreshCw,
  UserRound,
  CalendarDays,
  Building2,
} from "lucide-react";
import { toast } from "sonner";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";

import { getSupabaseClient } from "../providers/supabase/supabase";
import type { Company, Contact } from "../types";

// Sprint 10.x — Refonte spec Théo 2026-05-14 :
// "Comptes à closer" — alimenté par le score de chauffe IA.
// Source : vue `companies_heat_summary` (avg_heat_score, trigger_contact_id,
// is_paying_loose/strict). Toggle Élargi/Strict pour filtrer les payants.
// Filtres tier : pastilles cliquables (multi-select).
// Dropdown ⋮ par ligne : fiche company, fiche contact déclencheur, dater
// prochaine action, refresh score (invoke compute-heat-score).

type HeatRow = {
  id: number;
  company_id: number;
  avg_heat_score: number | null;
  scored_contact_count: number;
  total_contact_count: number;
  trigger_contact_id: number | null;
  trigger_delta: number | null;
  trigger_event_at: string | null;
  is_fresh: boolean;
  payment_state: string;
  is_paying_loose: boolean;
  is_paying_strict: boolean;
};

type HeatTier = "brulant" | "chaud" | "tiede" | "froid" | "mort" | "unrated";

const TIER_META: Record<HeatTier, { emoji: string; label: string }> = {
  brulant: { emoji: "🔥", label: "brulant" },
  chaud: { emoji: "🌶️", label: "chaud" },
  tiede: { emoji: "🟡", label: "tiede" },
  froid: { emoji: "🧊", label: "froid" },
  mort: { emoji: "⚫", label: "mort" },
  unrated: { emoji: "·", label: "non noté" },
};

// Cohérent avec l'edge function compute-heat-score :
// 81-100 brulant · 61-80 chaud · 41-60 tiede · 21-40 froid · 0-20 mort
function scoreToTier(score: number | null | undefined): HeatTier {
  if (score == null) return "unrated";
  if (score >= 81) return "brulant";
  if (score >= 61) return "chaud";
  if (score >= 41) return "tiede";
  if (score >= 21) return "froid";
  return "mort";
}

const FILTERABLE_TIERS: HeatTier[] = ["brulant", "chaud", "tiede"];

type PaymentMode = "loose" | "strict";

function relTime(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return formatDistanceToNowStrict(new Date(iso), {
      locale: fr,
      addSuffix: true,
    });
  } catch {
    return "";
  }
}

export const MyDayCompaniesToClose = () => {
  const [mode, setMode] = useState<PaymentMode>("loose");
  const [tierFilter, setTierFilter] = useState<HeatTier[]>([]);

  // Backend: vue companies_heat_summary, tri avg_heat_score DESC NULLS LAST,
  // payant exclu via is_paying_loose/strict.
  const filter: Record<string, unknown> = {};
  if (mode === "loose") {
    filter["is_paying_loose@eq"] = false;
  } else {
    filter["is_paying_strict@eq"] = false;
  }

  // Fetch volontairement large (30) : on filtre tier côté front pour ne pas
  // multiplier les requêtes quand l'utilisateur toggle ; on tronque ensuite à 10.
  const { data: rawRows, isPending } = useGetList<HeatRow>(
    "companies_heat_summary",
    {
      pagination: { page: 1, perPage: 30 },
      sort: { field: "avg_heat_score", order: "DESC" },
      filter,
    },
  );

  // PostgREST renvoie id = company_id pour cette vue mais on travaille avec
  // company_id explicitement pour clarté.
  const rows = useMemo(() => rawRows ?? [], [rawRows]);

  const filteredRows = useMemo(() => {
    let result = rows;
    if (tierFilter.length > 0) {
      const allowed = new Set<HeatTier>(tierFilter);
      result = result.filter((r) => allowed.has(scoreToTier(r.avg_heat_score)));
    }
    return result.slice(0, 10);
  }, [rows, tierFilter]);

  // Hydrate company names + trigger contacts via batched getMany.
  const companyIds = useMemo(
    () => Array.from(new Set(filteredRows.map((r) => r.company_id))),
    [filteredRows],
  );
  const triggerContactIds = useMemo(
    () =>
      Array.from(
        new Set(
          filteredRows
            .map((r) => r.trigger_contact_id)
            .filter((id): id is number => id != null),
        ),
      ),
    [filteredRows],
  );

  const { data: companies } = useGetMany<Company>(
    "companies",
    { ids: companyIds },
    { enabled: companyIds.length > 0 },
  );
  const { data: triggerContacts } = useGetMany<Contact>(
    "contacts_summary",
    { ids: triggerContactIds },
    { enabled: triggerContactIds.length > 0 },
  );

  const companyById = useMemo(() => {
    const m = new Map<number, Company>();
    (companies ?? []).forEach((c) => m.set(c.id as number, c));
    return m;
  }, [companies]);
  const contactById = useMemo(() => {
    const m = new Map<number, Contact>();
    (triggerContacts ?? []).forEach((c) => m.set(c.id as number, c));
    return m;
  }, [triggerContacts]);

  // Empty states: distingue "rien à pousser" vs "scoring pas encore passé".
  const allUnrated =
    rows.length > 0 && rows.every((r) => r.avg_heat_score == null);

  return (
    <Card>
      <CardContent className="py-4 md:py-5">
        <div className="flex items-center justify-between gap-2 mb-3">
          <div className="flex items-center gap-2 min-w-0">
            <Flame className="size-4 text-fabrik-red shrink-0" />
            <p className="text-[13px] font-semibold text-foreground truncate">
              Comptes à closer
            </p>
            {filteredRows.length ? (
              <Badge variant="secondary" className="text-[11px] shrink-0">
                {filteredRows.length}
              </Badge>
            ) : null}
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <Select
              value={mode}
              onValueChange={(v) => setMode(v as PaymentMode)}
            >
              <SelectTrigger
                size="sm"
                className="h-7 text-[11.5px] min-w-[90px]"
                aria-label="Mode de filtrage payants"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="loose">Élargi</SelectItem>
                <SelectItem value="strict">Strict</SelectItem>
              </SelectContent>
            </Select>
            <ToggleGroup
              type="multiple"
              size="sm"
              value={tierFilter}
              onValueChange={(v) => setTierFilter(v as HeatTier[])}
              aria-label="Filtrer par tier"
            >
              {FILTERABLE_TIERS.map((tier) => (
                <ToggleGroupItem
                  key={tier}
                  value={tier}
                  className="h-7 px-2 text-[12.5px]"
                  aria-label={TIER_META[tier].label}
                >
                  {TIER_META[tier].emoji}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </div>
        </div>

        {isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : allUnrated ? (
          <UnratedEmptyState companyIds={companyIds} />
        ) : !filteredRows.length ? (
          <p className="text-[12.5px] text-muted-foreground">
            Aucun compte à pousser pour l'instant. Continue tes relances 🚀
          </p>
        ) : (
          <ul className="flex flex-col divide-y divide-border/40">
            {filteredRows.map((row) => (
              <CompanyRow
                key={row.company_id}
                row={row}
                company={companyById.get(row.company_id)}
                triggerContact={
                  row.trigger_contact_id
                    ? contactById.get(row.trigger_contact_id)
                    : undefined
                }
              />
            ))}
          </ul>
        )}
      </CardContent>
    </Card>
  );
};

function UnratedEmptyState({ companyIds }: { companyIds: number[] }) {
  const [refreshing, setRefreshing] = useState(false);

  const handleForceCompute = async () => {
    if (refreshing) return;
    setRefreshing(true);
    try {
      const { error } = await getSupabaseClient().functions.invoke(
        "compute-heat-score",
        { body: {} },
      );
      if (error) throw error;
      toast.success("Score en cours de recalcul…");
    } catch (err) {
      console.error("compute-heat-score.error", err);
      toast.error("Impossible de lancer le calcul");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <p className="text-[12.5px] text-muted-foreground">
        Score en cours de calcul…
      </p>
      <Button
        type="button"
        variant="outline"
        size="sm"
        className="w-fit gap-1.5"
        onClick={handleForceCompute}
        disabled={refreshing || companyIds.length === 0}
      >
        <RefreshCw className={`size-3.5 ${refreshing ? "animate-spin" : ""}`} />
        Forcer le calcul
      </Button>
    </div>
  );
}

function CompanyRow({
  row,
  company,
  triggerContact,
}: {
  row: HeatRow;
  company: Company | undefined;
  triggerContact: Contact | undefined;
}) {
  const [actionDialogOpen, setActionDialogOpen] = useState(false);

  const tier = scoreToTier(row.avg_heat_score);
  const tierMeta = TIER_META[tier];
  const avgLabel =
    row.avg_heat_score != null
      ? `avg ${Math.round(Number(row.avg_heat_score))}`
      : "non noté";

  const companyName = company?.name ?? `Compte #${row.company_id}`;
  const showTrigger = !!row.trigger_contact_id && !!row.trigger_event_at;

  const contactName = triggerContact
    ? [triggerContact.first_name, triggerContact.last_name]
        .filter(Boolean)
        .join(" ") || "Contact"
    : "Contact";

  const triggerDelta = row.trigger_delta ?? 0;
  const triggerSign = triggerDelta > 0 ? "+" : "";
  // heat_score est exposé dans contacts_summary mais pas (encore) sur le type
  // Contact (types DB générés sont read-only pour le frontend-agent).
  const triggerScore =
    (triggerContact as (Contact & { heat_score?: number | null }) | undefined)
      ?.heat_score ?? null;
  const triggerWhen = relTime(row.trigger_event_at);

  const refreshScore = async () => {
    try {
      const body = row.trigger_contact_id
        ? { contact_id: row.trigger_contact_id }
        : { company_id: row.company_id };
      const { error } = await getSupabaseClient().functions.invoke(
        "compute-heat-score",
        { body },
      );
      if (error) throw error;
      toast.success("Score en cours de recalcul…");
    } catch (err) {
      console.error("compute-heat-score.error", err);
      toast.error("Impossible de recalculer le score");
    }
  };

  return (
    <>
      <li className="py-2 first:pt-0 last:pb-0">
        <div className="flex items-start gap-3">
          <Link
            to={`/companies/${row.company_id}/show`}
            className="min-w-0 flex-1 hover:opacity-80"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span aria-hidden className="text-[14px] leading-none">
                {tierMeta.emoji}
              </span>
              <p className="text-sm font-medium truncate flex-1">
                {companyName}
              </p>
              <span className="text-[11.5px] text-muted-foreground tabular-nums shrink-0">
                {avgLabel}
              </span>
              {row.is_fresh && (
                <Badge
                  variant="outline"
                  className="text-[10px] px-1 py-0 h-4 text-fabrik-red border-fabrik-red shrink-0"
                >
                  NEW
                </Badge>
              )}
            </div>
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {showTrigger ? (
                <>
                  <UserRound className="inline size-3 mr-1 -mt-0.5" />
                  {contactName} a déclenché {triggerSign}
                  {triggerDelta}
                  {triggerScore != null ? ` (${triggerScore})` : ""}
                  {triggerWhen ? ` · ${triggerWhen}` : ""}
                </>
              ) : (
                <>
                  {row.scored_contact_count} contact
                  {row.scored_contact_count > 1 ? "s" : ""} scoré
                  {row.scored_contact_count > 1 ? "s" : ""}
                </>
              )}
            </p>
          </Link>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="size-7 shrink-0 text-fabrik-text-3 hover:text-foreground"
                aria-label="Plus d'actions"
              >
                <MoreHorizontal className="size-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuItem asChild>
                <Link to={`/companies/${row.company_id}/show`}>
                  <Building2 className="size-3.5" />
                  Voir la fiche company
                </Link>
              </DropdownMenuItem>
              {row.trigger_contact_id && (
                <DropdownMenuItem asChild>
                  <Link to={`/contacts/${row.trigger_contact_id}/show`}>
                    <UserRound className="size-3.5" />
                    Voir le contact déclencheur
                  </Link>
                </DropdownMenuItem>
              )}
              {row.trigger_contact_id && (
                <DropdownMenuItem onClick={() => setActionDialogOpen(true)}>
                  <CalendarDays className="size-3.5" />
                  Dater prochaine action
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={refreshScore}>
                <RefreshCw className="size-3.5" />
                Refresh score
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </li>
      {row.trigger_contact_id && triggerContact && (
        <DateNextActionDialog
          open={actionDialogOpen}
          onOpenChange={setActionDialogOpen}
          contact={triggerContact}
        />
      )}
    </>
  );
}

function ymd(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

function DateNextActionDialog({
  open,
  onOpenChange,
  contact,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  contact: Contact;
}) {
  const initialDate =
    contact.next_action_date && contact.next_action_date.length >= 10
      ? contact.next_action_date.slice(0, 10)
      : ymd(new Date(Date.now() + 86400 * 1000)); // J+1 par défaut
  const [date, setDate] = useState(initialDate);
  const [update, { isPending }] = useUpdate();
  const notify = useNotify();

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!date) return;
    update(
      "contacts",
      {
        id: contact.id,
        data: { next_action_date: date },
        previousData: contact,
      },
      {
        mutationMode: "optimistic",
        onSuccess: () => {
          notify("Date de prochaine action mise à jour", { type: "success" });
          onOpenChange(false);
        },
        onError: () =>
          notify("Impossible de mettre à jour la date", { type: "error" }),
      },
    );
  };

  const contactName =
    [contact.first_name, contact.last_name].filter(Boolean).join(" ") ||
    "ce contact";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>Dater prochaine action</DialogTitle>
          <DialogDescription>
            Sur <strong>{contactName}</strong>. La date apparaîtra dans "Actions
            du jour" à l'échéance.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="flex flex-col gap-3 mt-2">
          <div>
            <Label htmlFor="mdctc-date" className="text-[12px]">
              Échéance
            </Label>
            <Input
              id="mdctc-date"
              type="date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="mt-1"
              required
            />
          </div>
          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Annuler
            </Button>
            <Button type="submit" disabled={isPending || !date}>
              {isPending ? "Enregistrement…" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
